import { Checkbox } from '@roger/ui-components';
import { useOptionalMultiselectField } from './MultiselectFieldContext';
import { useRepeatBindings } from './repeatBindings';
import {
  joinClasses,
  BESPOKE_CHECKBOX_CLASS,
  BESPOKE_CONTROL_ROW_CLASS,
  BESPOKE_OPTION_LABEL_CLASS,
  BESPOKE_OPTION_LABEL_DISABLED_CLASS,
} from './fieldClassSlots';
import { resolveControlClassName } from './resolveFieldClassNames';

export interface OASISMultiselectOptionProps {
  /** Literal schema option value for fixed layouts such as table cells. */
  value?: string;
  /** Repeat binding alias (e.g. `opt`) — resolves to `{ value, label }` from `repeat` scope */
  valueFrom?: string;
  /** Display-only replacement for the schema option label; `value` remains authoritative. */
  labelOverride?: string;
  /** Row wrapper (checkbox + label) */
  className?: string;
  style?: React.CSSProperties;
  /** Classes on checkbox label text */
  labelClassName?: string;
  optionRowClassName?: string;
  controlClassName?: string;
  /** @deprecated Prefer `controlClassName`. */
  checkboxClassName?: string;
}

/** One enumerated option row for compound `OASISMultiselect` layouts. */
export function OASISMultiselectOption({
  value,
  valueFrom,
  labelOverride,
  className,
  style,
  labelClassName,
  optionRowClassName,
  controlClassName,
  checkboxClassName,
}: OASISMultiselectOptionProps) {
  const field = useOptionalMultiselectField();
  const scope = useRepeatBindings();

  if (!field) {
    return null;
  }

  const repeatedItem = valueFrom ? scope[valueFrom] : undefined;
  const literalItem = value
    ? {
        value,
        label:
          labelOverride ??
          field.options.find(opt => opt.value === value)?.label ??
          value,
      }
    : undefined;
  const item =
    literalItem ??
    (repeatedItem
      ? {
          ...repeatedItem,
          label: labelOverride ?? repeatedItem.label,
        }
      : undefined);

  if (!item) {
    return null;
  }

  const id = `${field.qid}-${item.value}`;
  const checked = field.values.includes(item.value);
  const optionDisabled = field.disabled === true;
  const resolvedRowClassName = joinClasses(
    BESPOKE_CONTROL_ROW_CLASS,
    optionRowClassName,
    className
  );
  const resolvedCheckboxClassName = joinClasses(
    BESPOKE_CHECKBOX_CLASS,
    resolveControlClassName(controlClassName, checkboxClassName)
  );

  return (
    <div className={resolvedRowClassName} style={style}>
      <Checkbox
        id={id}
        checked={checked}
        disabled={optionDisabled}
        onCheckedChange={() => field.toggle(item.value)}
        className={resolvedCheckboxClassName}
      />
      <label
        htmlFor={id}
        className={joinClasses(
          BESPOKE_OPTION_LABEL_CLASS,
          optionDisabled && BESPOKE_OPTION_LABEL_DISABLED_CLASS,
          labelClassName
        )}
      >
        {item.label}
      </label>
    </div>
  );
}
